import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import ConfirmModal from "../common/ConfirmModal";
import PromptModal from "../common/PromptModal";
import WorkspaceUploadModal from "./WorkspaceUploadModal";
import WorkspaceShareModal from "./WorkspaceShareModal";
import {
  archiveWorkspaceFolder,
  copyFiling,
  createWorkspaceFolder,
  emptyTrash,
  fileDocument,
  getWorkspaceFolderDocuments,
  getWorkspaceTrash,
  getWorkspaceTree,
  moveFiling,
  purgeFromTrash,
  renameFiling,
  renameWorkspaceFolder,
  restoreFromTrash,
  unfileDocument,
  uploadToWorkspace,
} from "../../api/workspaceApi";
import "./workspace.css";

/** The drag payload for a document, so a drop knows what it is being handed. */
export const WS_DRAG_TYPE = "application/x-depository-document";

/**
 * MY WORKSPACE — the top layer of the three-layer content model.
 *
 * A person's own folders, the documents they have filed there, and the way new documents get in.
 * Every row carries the other two layers as context — the document base its bytes live in, the
 * company folder it is filed under, the subjects it carries — because the point of the personal layer
 * is to be a lens over the one archive, not a second place documents can hide.
 *
 * Filing here is a pointer. Removing a document from a folder unfiles it and leaves the archived
 * record alone: destroying a record is a governed disposition, never a drag gesture.
 *
 * Nothing here removes anything without asking first, and nothing removed is gone: both gestures ask for
 * confirmation and then land in the recycle bin, which is a view of this same Explorer rather than a
 * screen somewhere else — the place you undo a mistake should be next to where you made it.
 */
export default function WorkspaceExplorer({ onOpenDocument }) {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();

  const [tree, setTree] = useState({ folders: [], sharedWithMe: [], owner: null });
  const [cwd, setCwd] = useState(null);           // null ⇒ the workspace root
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docsLoading, setDocsLoading] = useState(false);
  const [error, setError] = useState("");
  const [dropOver, setDropOver] = useState(null);  // folder id, or "body" for the current folder
  const [uploadOpen, setUploadOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [unfiling, setUnfiling] = useState(null);     // the document awaiting "yes, remove it"
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState({ documents: [], folders: [] });
  const [trashLoading, setTrashLoading] = useState(false);
  const [purging, setPurging] = useState(null);       // one bin entry awaiting "yes, for good"
  const [emptying, setEmptying] = useState(false);
  const [busy, setBusy] = useState(false);
  const dragged = useRef(null);

  // Memoised so the fallback [] is not a fresh array on every render, which would re-run every
  // derivation below it.
  const folders = useMemo(() => tree.folders || [], [tree.folders]);
  const byId = useMemo(() => {
    const m = new Map();
    folders.forEach((f) => m.set(f.id, f));
    (tree.sharedWithMe || []).forEach((s) => m.set(s.folder.id, s.folder));
    return m;
  }, [folders, tree.sharedWithMe]);

  const current = cwd != null ? byId.get(cwd) : null;

  // A folder someone shared with me is read-only unless the share says otherwise; my own always writable.
  const sharedEntry = useMemo(
    () => (tree.sharedWithMe || []).find((s) => s.folder.id === cwd) || null,
    [tree.sharedWithMe, cwd]
  );
  const isMine = current ? !sharedEntry && current.ownerUserName === tree.owner : true;
  const canWrite = current ? (isMine || !!sharedEntry?.canWrite) : false;

  const loadTree = useCallback(async () => {
    if (!token) return;
    try {
      const res = await getWorkspaceTree(token, i18n?.language);
      setTree(res || { folders: [], sharedWithMe: [] });
      setError("");
    } catch (e) {
      setError(e?.message || t("workspace.loadFailed", { defaultValue: "Your workspace could not be loaded." }));
    } finally {
      setLoading(false);
    }
  }, [token, i18n?.language, t]);

  useEffect(() => { loadTree(); }, [loadTree]);

  const loadDocs = useCallback(async (folderId) => {
    if (folderId == null) { setDocs([]); return; }
    setDocsLoading(true);
    try {
      const res = await getWorkspaceFolderDocuments(token, folderId);
      setDocs(res?.documents || []);
    } catch {
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  }, [token]);

  useEffect(() => { loadDocs(cwd); }, [cwd, loadDocs]);

  // The bin is loaded on every refresh, not only when it is open, so the header can say how much is in
  // it — a bin you have to open to discover is not much of a safety net.
  const loadTrash = useCallback(async () => {
    if (!token) return;
    setTrashLoading(true);
    try {
      const res = await getWorkspaceTrash(token);
      setTrash({ documents: res?.documents || [], folders: res?.folders || [] });
    } catch {
      setTrash({ documents: [], folders: [] });
    } finally {
      setTrashLoading(false);
    }
  }, [token]);

  useEffect(() => { loadTrash(); }, [loadTrash]);

  const refresh = useCallback(() => {
    loadTree();
    loadDocs(cwd);
    loadTrash();
  }, [loadTree, loadDocs, cwd, loadTrash]);

  const trashCount = (trash.documents?.length || 0) + (trash.folders?.length || 0);

  // ── drag and drop ─────────────────────────────────────────────────────────────────────────────
  // Three gestures land here: a filing dragged between my folders (move, or copy while holding a
  // modifier), a search result dragged in from the results list (file it), and an OS file dropped
  // from the desktop (upload it). They are told apart by what the drop actually carries.

  const onDropInto = useCallback(async (folderId, e) => {
    e.preventDefault();
    setDropOver(null);
    const target = byId.get(folderId);
    if (!target) return;

    const osFiles = Array.from(e.dataTransfer?.files || []);
    if (osFiles.length > 0) {
      try {
        for (const f of osFiles) {
          await uploadToWorkspace(token, { file: f, personalFolderId: folderId });
        }
        refresh();
      } catch (err) {
        setError(err?.message || t("workspace.uploadFailed", { defaultValue: "The document could not be uploaded." }));
      }
      return;
    }

    let payload = dragged.current;
    if (!payload) {
      const raw = e.dataTransfer?.getData(WS_DRAG_TYPE) || e.dataTransfer?.getData("text/plain");
      try { payload = raw ? JSON.parse(raw) : null; } catch { payload = null; }
    }
    if (!payload) return;

    try {
      if (payload.filingId != null) {
        // Already filed somewhere of mine — a move, unless a copy was asked for.
        if (payload.copy) await copyFiling(token, payload.filingId, folderId);
        else await moveFiling(token, payload.filingId, folderId);
      } else if (payload.dbName) {
        await fileDocument(token, {
          personalFolderId: folderId,
          dbName: payload.dbName,
          archiveNo: payload.archiveNo || null,
          documentId: payload.documentId ?? null,
        });
      }
      refresh();
    } catch (err) {
      setError(err?.message || t("workspace.fileFailed", { defaultValue: "The document could not be filed." }));
    } finally {
      dragged.current = null;
    }
  }, [byId, token, refresh, t]);

  const startDrag = useCallback((doc) => (e) => {
    // altKey/ctrlKey is the familiar "copy instead of move" modifier; read at drag start because the
    // drop event does not reliably carry modifier state on every browser.
    const payload = {
      filingId: doc.filingId,
      dbName: doc.dbName,
      archiveNo: doc.archiveNo,
      documentId: doc.id,
      copy: e.altKey || e.ctrlKey || e.metaKey,
    };
    dragged.current = payload;
    try {
      e.dataTransfer.setData(WS_DRAG_TYPE, JSON.stringify(payload));
      e.dataTransfer.setData("text/plain", JSON.stringify(payload));
      e.dataTransfer.effectAllowed = payload.copy ? "copy" : "move";
    } catch { /* older browsers restrict custom types; dragged.current still carries it */ }
  }, []);

  // ── folder actions ────────────────────────────────────────────────────────────────────────────

  const createFolder = useCallback(async (name) => {
    try {
      await createWorkspaceFolder(token, { name, parentId: cwd });
      setNewFolderOpen(false);
      loadTree();
    } catch (e) {
      setError(e?.message || t("workspace.folderFailed", { defaultValue: "The folder could not be created." }));
      setNewFolderOpen(false);
    }
  }, [token, cwd, loadTree, t]);

  const doRename = useCallback(async (name) => {
    const target = renaming;
    setRenaming(null);
    if (!target) return;
    try {
      await renameWorkspaceFolder(token, target.id, { name });
      loadTree();
    } catch (e) {
      setError(e?.message || t("workspace.renameFailed", { defaultValue: "The folder could not be renamed." }));
    }
  }, [renaming, token, loadTree, t]);

  const doArchive = useCallback(async () => {
    const target = archiving;
    setArchiving(null);
    if (!target) return;
    try {
      await archiveWorkspaceFolder(token, target.id);
      if (cwd === target.id) setCwd(target.parentId ?? null);
      loadTree();
    } catch (e) {
      setError(e?.message || t("workspace.removeFolderFailed", { defaultValue: "The folder could not be removed." }));
    }
  }, [archiving, token, cwd, loadTree, t]);

  /**
   * Renames a document IN THIS FOLDER. The label lives on the filing, so the archived record keeps the
   * file name it was stored under and a colleague looking at the same document still sees that name.
   * An empty answer clears the label rather than storing a blank one, which is the only way back.
   */
  const doRenameDoc = useCallback(async (name) => {
    const target = renamingDoc;
    setRenamingDoc(null);
    if (!target) return;
    try {
      const res = await renameFiling(token, target.filingId, name);
      const label = res?.displayName || null;
      setDocs((prev) => prev.map((d) => (
        d.filingId === target.filingId
          ? { ...d, displayName: label, fileName: label || d.originalFileName }
          : d
      )));
    } catch (e) {
      setError(e?.message || t("workspace.renameDocFailed", { defaultValue: "The document could not be renamed." }));
    }
  }, [renamingDoc, token, t]);

  const doUnfile = useCallback(async () => {
    const doc = unfiling;
    setUnfiling(null);
    if (!doc) return;
    try {
      await unfileDocument(token, doc.filingId);
      setDocs((prev) => prev.filter((d) => d.filingId !== doc.filingId));
      loadTrash();
    } catch (e) {
      setError(e?.message || t("workspace.unfileFailed", { defaultValue: "The document could not be removed from this folder." }));
    }
  }, [unfiling, token, loadTrash, t]);

  // ── the recycle bin ───────────────────────────────────────────────────────────────────────────

  const restore = useCallback(async (entry) => {
    setBusy(true);
    try {
      const res = await restoreFromTrash(
        token,
        entry.kind === "folder" ? { folderIds: [entry.id] } : { filingIds: [entry.id] }
      );
      // A partial result is the normal case for a batch, so the reason is shown rather than a silent
      // no-op that looks like the button did nothing.
      const blocked = (res?.blocked || [])[0];
      if (blocked) setError(blocked.message);
      else if ((res?.movedToRoot || []).length) {
        setError(t("workspace.restoredToRoot", {
          defaultValue:
            "The folder it used to be in is in the bin too, so it came back at the top of your workspace.",
        }));
      }
      refresh();
    } catch (e) {
      setError(e?.message || t("workspace.restoreFailed", { defaultValue: "That could not be restored." }));
    } finally {
      setBusy(false);
    }
  }, [token, refresh, t]);

  const doPurge = useCallback(async () => {
    const entry = purging;
    setPurging(null);
    if (!entry) return;
    setBusy(true);
    try {
      await purgeFromTrash(
        token,
        entry.kind === "folder" ? { folderIds: [entry.id] } : { filingIds: [entry.id] }
      );
      loadTrash();
    } catch (e) {
      setError(e?.message || t("workspace.purgeFailed", { defaultValue: "That could not be deleted." }));
    } finally {
      setBusy(false);
    }
  }, [purging, token, loadTrash, t]);

  const doEmpty = useCallback(async () => {
    setEmptying(false);
    setBusy(true);
    try {
      await emptyTrash(token);
      loadTrash();
    } catch (e) {
      setError(e?.message || t("workspace.purgeFailed", { defaultValue: "That could not be deleted." }));
    } finally {
      setBusy(false);
    }
  }, [token, loadTrash, t]);

  // ── render ────────────────────────────────────────────────────────────────────────────────────

  // What the overflow menu holds: the folder's own settings, then the recycle bin as a destination.
  // The bin is last and separated because it is somewhere you GO, not something you do to this folder.
  const menuItems = useMemo(() => {
    const out = [];
    if (cwd != null && isMine) {
      out.push({
        key: "share",
        label: t("workspace.share", { defaultValue: "Share" }),
        icon: <ShareIcon />,
        onClick: () => setShareOpen(true),
      });
      out.push({
        key: "rename",
        label: t("workspace.rename", { defaultValue: "Rename" }),
        icon: <PencilIcon />,
        onClick: () => setRenaming(current),
      });
      if (!current?.isSystem) {
        out.push({
          key: "remove",
          label: t("workspace.removeFolder", { defaultValue: "Remove folder" }),
          icon: <TrashIcon />,
          danger: true,
          onClick: () => setArchiving(current),
        });
      }
      out.push({ key: "sep", separator: true });
    }
    out.push({
      key: "trash",
      label: t("workspace.trash", { defaultValue: "Recycle bin" }),
      icon: <BinIcon />,
      count: trashCount,
      onClick: () => setTrashOpen(true),
    });
    return out;
  }, [cwd, isMine, current, trashCount, t]);

  const children = useMemo(() => {
    if (cwd == null) {
      const roots = folders.filter((f) => f.parentId == null);
      return roots.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name || "").localeCompare(b.name || ""));
    }
    return folders
      .filter((f) => f.parentId === cwd)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name || "").localeCompare(b.name || ""));
  }, [folders, cwd]);

  const crumbs = useMemo(() => {
    const chain = [];
    let c = cwd != null ? byId.get(cwd) : null;
    let guard = 0;
    while (c && guard++ < 100) {
      chain.unshift(c);
      c = c.parentId != null ? byId.get(c.parentId) : null;
    }
    return chain;
  }, [cwd, byId]);

  if (loading) {
    return <div className="ws-empty">{t("workspace.loading", { defaultValue: "Opening your workspace…" })}</div>;
  }

  const sharedRoots = tree.sharedWithMe || [];
  const showSharedSection = cwd == null && sharedRoots.length > 0;

  return (
    <div>
      {error && <div className="ws-error" onClick={() => setError("")}>{error}</div>}

      <div className="ws-head">
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <button
            className="ws-crumb"
            onClick={() => { setTrashOpen(false); setCwd(null); }}
            style={crumbStyle(cwd == null && !trashOpen)}
          >
            {t("workspace.home", { defaultValue: "My Workspace" })}
          </button>
          {trashOpen ? (
            <>
              <span style={{ color: "#9ca3af" }}>›</span>
              <button className="ws-crumb" onClick={() => {}} style={crumbStyle(true)}>
                {t("workspace.trash", { defaultValue: "Recycle bin" })}
              </button>
            </>
          ) : (
            crumbs.map((c) => (
              <React.Fragment key={c.id}>
                <span style={{ color: "#9ca3af" }}>›</span>
                <button className="ws-crumb" onClick={() => setCwd(c.id)} style={crumbStyle(c.id === cwd)}>
                  {c.name}
                </button>
              </React.Fragment>
            ))
          )}
        </div>

        <div className="ws-head__actions">
          {trashOpen ? (
            <>
              <button className="ws-btn" onClick={() => setTrashOpen(false)}>
                {t("workspace.backToWorkspace", { defaultValue: "Back to my workspace" })}
              </button>
              {trashCount > 0 && (
                <button className="ws-btn ws-btn--danger" onClick={() => setEmptying(true)} disabled={busy}>
                  {t("workspace.emptyTrash", { defaultValue: "Empty bin" })}
                </button>
              )}
            </>
          ) : (
            <>
              {/* Only the two things you came here to do stay on the bar. Everything else is about the
                  FOLDER rather than the work, and lives one click away. */}
              {cwd != null && canWrite && (
                <button className="ws-btn ws-btn--primary" onClick={() => setUploadOpen(true)}>
                  <UploadIcon />
                  {t("workspace.upload", { defaultValue: "Add document" })}
                </button>
              )}
              {(cwd == null || isMine) && (
                <button className="ws-btn" onClick={() => setNewFolderOpen(true)}>
                  <PlusIcon />
                  {t("workspace.newFolder", { defaultValue: "New folder" })}
                </button>
              )}
              <WorkspaceMenu t={t} badge={trashCount} items={menuItems} />
            </>
          )}
        </div>
      </div>

      {trashOpen ? (
        <TrashView
          trash={trash}
          loading={trashLoading}
          busy={busy}
          onRestore={restore}
          onPurge={setPurging}
          t={t}
        />
      ) : (
      <div
        className={`ws-dropzone${dropOver === "body" ? " ws-dropzone--over" : ""}`}
        onDragOver={(e) => { if (cwd != null && canWrite) { e.preventDefault(); setDropOver("body"); } }}
        onDragLeave={() => setDropOver((p) => (p === "body" ? null : p))}
        onDrop={(e) => { if (cwd != null && canWrite) onDropInto(cwd, e); }}
      >
        {children.length > 0 && (
          <div className="ws-tiles">
            {children.map((f) => (
              <button
                key={f.id}
                className={`ws-tile${dropOver === f.id ? " ws-tile--drop" : ""}`}
                title={f.path}
                onClick={() => setCwd(f.id)}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropOver(f.id); }}
                onDragLeave={() => setDropOver((p) => (p === f.id ? null : p))}
                onDrop={(e) => { e.stopPropagation(); onDropInto(f.id, e); }}
              >
                <FolderGlyph />
                <span className="ws-tile__name">{f.name}</span>
              </button>
            ))}
          </div>
        )}

        {showSharedSection && (
          <>
            <div className="ws-lens__hint" style={{ marginTop: 4 }}>
              {t("workspace.sharedWithMe", { defaultValue: "Shared with me" })}
            </div>
            <div className="ws-tiles">
              {sharedRoots.map((s) => (
                <button
                  key={s.folder.id}
                  className="ws-tile"
                  title={s.folder.path}
                  onClick={() => setCwd(s.folder.id)}
                >
                  <FolderGlyph shared />
                  <span className="ws-tile__name">{s.folder.name}</span>
                  <span className="ws-shared-owner">{s.ownerUserName}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {cwd != null && (
          docsLoading ? (
            <div className="ws-empty">{t("common.loading", { defaultValue: "Loading…" })}</div>
          ) : docs.length === 0 ? (
            <div className="ws-empty">
              {t("workspace.emptyFolder", { defaultValue: "Nothing filed here yet." })}
              {canWrite && (
                <div className="ws-empty__hint">
                  {t("workspace.emptyHint", {
                    defaultValue: "Drop a file here, or drag a document in from your search results.",
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="ws-docs">
              {docs.map((d) => (
                <div
                  key={d.filingId}
                  className="ws-doc"
                  draggable={canWrite}
                  onDragStart={startDrag(d)}
                  onDragEnd={() => { dragged.current = null; }}
                  onClick={() => onOpenDocument && onOpenDocument(d)}
                  title={d.originalFileName || d.fileName}
                >
                  <DocGlyph />
                  <div className="ws-doc__main">
                    <div className="ws-doc__name">{d.fileName || d.originalFileName}</div>
                    <div className="ws-doc__layers">
                      {/* Bottom layer — the document base holding the bytes. */}
                      <span className="ws-chip ws-chip--base" title={t("workspace.layerBase", { defaultValue: "Document base" })}>
                        <BaseIcon />{d.dbName}
                      </span>
                      {/* Middle layer — the company's placement and subjects. */}
                      {d.folderPath && (
                        <span className="ws-chip ws-chip--folder" title={t("workspace.layerOrgFolder", { defaultValue: "Company folder" })}>
                          <FolderMiniIcon />{d.folderPath}
                        </span>
                      )}
                      {/* A renamed row must still admit what the archive calls it, or the label quietly
                          becomes a second truth about the document. */}
                      {d.displayName && d.originalFileName && d.displayName !== d.originalFileName && (
                        <span
                          className="ws-chip ws-chip--orig"
                          title={t("workspace.archivedAs", { defaultValue: "Archived as" })}
                        >
                          <DocMiniIcon />{d.originalFileName}
                        </span>
                      )}
                      {(d.taxonomy || []).map((term) => (
                        <span key={term.id} className="ws-chip ws-chip--term" title={t("workspace.layerTerm", { defaultValue: "Subject" })}>
                          <TagIcon />{term.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="ws-doc__date">{formatDate(d.documentDate || d.addedAt)}</span>
                  {canWrite && (
                    <>
                      <button
                        type="button"
                        className="ws-doc__act"
                        title={t("workspace.renameDoc", { defaultValue: "Rename in this folder" })}
                        onClick={(e) => { e.stopPropagation(); setRenamingDoc(d); }}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        className="ws-doc__remove"
                        title={t("workspace.unfile", { defaultValue: "Remove from this folder" })}
                        onClick={(e) => { e.stopPropagation(); setUnfiling(d); }}
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {cwd == null && children.length === 0 && !showSharedSection && (
          <div className="ws-empty">
            {t("workspace.emptyWorkspace", { defaultValue: "Your workspace is empty." })}
            <div className="ws-empty__hint">
              {t("workspace.emptyWorkspaceHint", { defaultValue: "Create a folder to get started." })}
            </div>
          </div>
        )}
      </div>
      )}

      <WorkspaceUploadModal
        open={uploadOpen}
        folder={current}
        onClose={() => setUploadOpen(false)}
        onUploaded={refresh}
      />

      <WorkspaceShareModal
        open={shareOpen}
        folder={current}
        onClose={() => setShareOpen(false)}
        onChanged={loadTree}
      />

      <PromptModal
        open={newFolderOpen}
        title={t("workspace.newFolder", { defaultValue: "New folder" })}
        label={t("workspace.folderName", { defaultValue: "Folder name" })}
        icon="📁"
        confirmLabel={t("common.create", { defaultValue: "Create" })}
        maxLength={200}
        onSubmit={createFolder}
        onCancel={() => setNewFolderOpen(false)}
      />

      <PromptModal
        open={!!renamingDoc}
        title={t("workspace.renameDoc", { defaultValue: "Rename in this folder" })}
        description={t("workspace.renameDocHint", {
          defaultValue:
            "This is your own name for the document in this folder. The archived file keeps the name it was stored under, and colleagues still see that one. Leave it empty to go back to it.",
        })}
        label={t("workspace.docName", { defaultValue: "Name in this folder" })}
        defaultValue={renamingDoc?.displayName || renamingDoc?.fileName || renamingDoc?.originalFileName || ""}
        placeholder={renamingDoc?.originalFileName || ""}
        icon="✏️"
        required={false}
        confirmLabel={t("common.save", { defaultValue: "Save" })}
        maxLength={400}
        onSubmit={doRenameDoc}
        onCancel={() => setRenamingDoc(null)}
      />

      <PromptModal
        open={!!renaming}
        title={t("workspace.rename", { defaultValue: "Rename" })}
        label={t("workspace.folderName", { defaultValue: "Folder name" })}
        defaultValue={renaming?.name || ""}
        icon="✏️"
        confirmLabel={t("common.save", { defaultValue: "Save" })}
        maxLength={200}
        onSubmit={doRename}
        onCancel={() => setRenaming(null)}
      />

      <ConfirmModal
        open={!!archiving}
        title={t("workspace.removeFolder", { defaultValue: "Remove folder" })}
        name={archiving?.name}
        body={t("workspace.removeFolderBody", {
          defaultValue:
            "The folder and anything under it are removed from your workspace. The documents themselves stay in the archive.",
        })}
        variant="warn"
        icon="🗂️"
        confirmLabel={t("common.remove", { defaultValue: "Remove" })}
        onConfirm={doArchive}
        onCancel={() => setArchiving(null)}
      />

      <ConfirmModal
        open={!!unfiling}
        title={t("workspace.unfile", { defaultValue: "Remove from this folder" })}
        name={unfiling?.fileName || unfiling?.originalFileName}
        body={t("workspace.unfileBody", {
          defaultValue:
            "The document leaves this folder and goes to your recycle bin, where you can put it back. It stays in the archive either way.",
        })}
        variant="warn"
        icon="🗑️"
        confirmLabel={t("common.remove", { defaultValue: "Remove" })}
        onConfirm={doUnfile}
        onCancel={() => setUnfiling(null)}
      />

      <ConfirmModal
        open={!!purging}
        title={t("workspace.purge", { defaultValue: "Delete permanently" })}
        name={purging?.name}
        body={
          purging?.kind === "folder"
            ? t("workspace.purgeFolderBody", {
                defaultValue:
                  "This folder and everything filed in it are removed from your workspace for good. The documents themselves stay in the archive.",
              })
            : t("workspace.purgeBody", {
                defaultValue:
                  "This entry is removed from your workspace for good and cannot be restored. The document itself stays in the archive.",
              })
        }
        variant="danger"
        icon="⚠️"
        confirmLabel={t("workspace.purge", { defaultValue: "Delete permanently" })}
        onConfirm={doPurge}
        onCancel={() => setPurging(null)}
      />

      <ConfirmModal
        open={emptying}
        title={t("workspace.emptyTrash", { defaultValue: "Empty bin" })}
        body={t("workspace.emptyTrashBody", {
          defaultValue:
            "Everything in your recycle bin is removed for good and cannot be restored. The documents themselves stay in the archive.",
        })}
        variant="danger"
        icon="⚠️"
        confirmLabel={t("workspace.emptyTrash", { defaultValue: "Empty bin" })}
        onConfirm={doEmpty}
        onCancel={() => setEmptying(false)}
      />
    </div>
  );
}

/**
 * The overflow menu behind the toolbar's "…".
 *
 * A workspace folder can be shared, renamed, removed and has a recycle bin behind it, and laying all of
 * that out as buttons put six controls above a folder that often holds one document — the chrome outweighed
 * the content. These are all rare, deliberate actions, so they belong one click away; the two things a
 * person actually came to do (add a document, make a folder) stay on the bar.
 *
 * The badge on the button is what stops the recycle bin from disappearing by being tidied away: when
 * something is waiting to be recovered, the "…" says so.
 */
function WorkspaceMenu({ items, badge, t }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const place = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    // Right-aligned to the button, like a native menu. Kept off the window edge on a narrow viewport.
    setPos({
      top: b.bottom + 6,
      right: Math.max(8, window.innerWidth - b.right),
      anchorTop: b.top,
      flipped: false,
    });
  }, []);

  // Flip above the button when the menu would run off the bottom of the window — measured, not guessed,
  // because the entry list changes with what the folder allows.
  useLayoutEffect(() => {
    if (!open || !pos || pos.flipped || !popRef.current) return;
    const h = popRef.current.offsetHeight;
    if (pos.top + h > window.innerHeight - 8 && pos.anchorTop - h - 6 > 8) {
      setPos((p) => ({ ...p, top: p.anchorTop - h - 6, flipped: true }));
    }
  }, [open, pos]);

  useEffect(() => {
    // Only while open, so a closed menu never swallows Escape for the rest of the page.
    if (!open) return undefined;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    // The menu is positioned against the viewport, so it would drift away from its button on a scroll.
    // Closing is the honest answer; re-anchoring mid-scroll only makes it chase the cursor.
    const onMove = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  const actionable = items.filter((i) => !i.separator);
  if (actionable.length === 0) return null;

  const toggle = () => {
    if (open) { setOpen(false); return; }
    place();
    setOpen(true);
  };

  // Rendered into the body rather than beside the button: the workspace sits inside the centre panel's
  // scroll box, which clips anything that overflows it — an absolutely-positioned menu was cut off at the
  // bottom edge, hiding its last entries. A portal is the only placement no ancestor can crop.
  const menu = open && pos ? createPortal(
    <div
      ref={popRef}
      className="ws-menu__pop"
      role="menu"
      style={{ top: pos.top, right: pos.right }}
    >
      {items.map((it) =>
        it.separator ? (
          <div key={it.key} className="ws-menu__sep" />
        ) : (
          <button
            key={it.key}
            type="button"
            role="menuitem"
            className={`ws-menu__item${it.danger ? " ws-menu__item--danger" : ""}`}
            onClick={() => { setOpen(false); it.onClick(); }}
          >
            {it.icon}
            <span className="ws-menu__label">{it.label}</span>
            {it.count > 0 && <span className="ws-badge">{it.count}</span>}
          </button>
        )
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="ws-menu">
      <button
        ref={btnRef}
        type="button"
        className={`ws-btn ws-menu__btn${badge > 0 ? " ws-btn--hasbin" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t("workspace.moreActions", { defaultValue: "More actions" })}
        onClick={toggle}
      >
        <DotsIcon />
        {badge > 0 && <span className="ws-badge">{badge}</span>}
      </button>
      {menu}
    </div>
  );
}

/**
 * The recycle bin: folders first, then documents, each saying where it came from and when it went.
 *
 * "Delete permanently" destroys a FILING — the row saying this document is kept here — and never the
 * document, which is why the wording says so on every one of these buttons. A person who has just been
 * told their delete is reversible needs to know exactly what the irreversible one does.
 */
function TrashView({ trash, loading, busy, onRestore, onPurge, t }) {
  const folders = trash.folders || [];
  const documents = trash.documents || [];

  if (loading && folders.length === 0 && documents.length === 0) {
    return <div className="ws-empty">{t("common.loading", { defaultValue: "Loading…" })}</div>;
  }

  if (folders.length === 0 && documents.length === 0) {
    return (
      <div className="ws-empty">
        {t("workspace.trashEmpty", { defaultValue: "Your recycle bin is empty." })}
        <div className="ws-empty__hint">
          {t("workspace.trashEmptyHint", {
            defaultValue: "Documents and folders you remove land here first, so you can put them back.",
          })}
        </div>
      </div>
    );
  }

  const row = (entry) => (
    <div key={`${entry.kind}-${entry.id}`} className="ws-doc ws-doc--trash">
      {entry.kind === "folder" ? <FolderMiniGlyph /> : <DocGlyph />}
      <div className="ws-doc__main">
        <div className="ws-doc__name">{entry.name}</div>
        <div className="ws-doc__layers">
          {entry.from && (
            <span className="ws-chip ws-chip--folder" title={t("workspace.trashFrom", { defaultValue: "Was in" })}>
              <FolderMiniIcon />{entry.from}
            </span>
          )}
          {entry.kind === "folder" && (
            <span className="ws-chip ws-chip--base">
              {t("workspace.trashFolderCount", {
                docs: entry.documentCount || 0,
                defaultValue: "Documents inside: {{docs}}",
              })}
            </span>
          )}
        </div>
      </div>
      <span className="ws-doc__date" title={entry.deletedBy || ""}>
        {formatDate(entry.deletedAt)}
      </span>
      <button className="ws-btn ws-btn--tiny" disabled={busy} onClick={() => onRestore(entry)}>
        {t("workspace.restore", { defaultValue: "Put back" })}
      </button>
      <button className="ws-btn ws-btn--tiny ws-btn--danger" disabled={busy} onClick={() => onPurge(entry)}>
        {t("workspace.purge", { defaultValue: "Delete permanently" })}
      </button>
    </div>
  );

  return (
    <div className="ws-dropzone">
      <div className="ws-lens__hint" style={{ marginTop: 4 }}>
        {t("workspace.trashHint", {
          defaultValue: "Documents and folders you removed. Nothing here is deleted from the archive.",
        })}
      </div>
      <div className="ws-docs">
        {folders.map((f) =>
          row({
            kind: "folder",
            id: f.id,
            name: f.name,
            from: parentPathOf(f.path),
            deletedAt: f.deletedAt,
            deletedBy: f.deletedBy,
            documentCount: f.documentCount,
          })
        )}
        {documents.map((d) =>
          row({
            kind: "filing",
            id: d.filingId,
            name: d.fileName || d.originalFileName,
            from: d.personalFolderPath,
            deletedAt: d.deletedAt,
            deletedBy: d.deletedBy,
          })
        )}
      </div>
    </div>
  );
}

/** "/My Documents/Contracts" ⇒ "/My Documents" — where a binned folder will go back to. */
function parentPathOf(path) {
  if (!path) return null;
  const cut = path.lastIndexOf("/");
  return cut > 0 ? path.slice(0, cut) : null;
}

function crumbStyle(active) {
  return {
    border: "none",
    background: "none",
    cursor: "pointer",
    color: active ? "#111827" : "#4338ca",
    fontWeight: 600,
    padding: 0,
    fontSize: 14,
  };
}

function formatDate(v) {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

/* ── glyphs (monochrome line art, matching the rest of the browse chrome) ─────────────────────── */

function FolderGlyph({ shared }) {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill={shared ? "#c8d6ea" : "#aebde0"} stroke="#8497c4" strokeWidth="0.75">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function DocGlyph() {
  return (
    <svg className="ws-doc__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

const MINI = { width: 11, height: 11, flex: "none" };

function DocMiniIcon() {
  return (
    <svg style={MINI} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
    </svg>
  );
}

function BaseIcon() {
  return (
    <svg style={MINI} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5" /><path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3" />
    </svg>
  );
}

function FolderMiniIcon() {
  return (
    <svg style={MINI} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg style={MINI} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.6 13.4 12 22l-9-9V3h10z" /><circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg className="ws-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M4 20h16" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="ws-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg className="ws-btn__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="19" cy="12" r="1.9" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg className="ws-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

/** The bin as a DESTINATION — distinct from TrashIcon, which marks the destructive action. */
function BinIcon() {
  return (
    <svg className="ws-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M6 6v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6" />
      <path d="m9.5 12 2.5 2.5L14.5 12" /><path d="M12 14.5V9" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="ws-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M6 6v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

/** A folder at document-row scale, so a binned folder and a binned document line up in the same list. */
function FolderMiniGlyph() {
  return (
    <svg className="ws-doc__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg className="ws-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}
